<template>
  <div class="p-faqs">
    <APageHead
      title="자주 묻는 질문 (FAQ)"
      sub="사용자들이 자주 묻는 질문과 답변을 확인합니다"
      icon="help"
    />

    <!-- 통계 카드 -->
    <div class="p-stats-grid">
      <AStat label="전체 FAQ" :value="faqs.length" icon="help_outline" hint="등록됨" />
      <AStat label="활성 FAQ" :value="activeFaqs" icon="check_circle" hint="공개 중" />
      <AStat label="카테고리" :value="categories.length" icon="category" hint="분류됨" />
      <AStat label="이번 달 조회" :value="monthlyViews" icon="visibility" hint="+12%" />
    </div>

    <!-- 필터 + 목록 -->
    <ACard :padding="0">
      <div class="p-faqs__filters">
        <div class="p-input-group p-input-group--lg">
          <i class="material-icons-round p-input-group__icon">search</i>
          <input
            type="text"
            class="p-input"
            placeholder="질문이나 답변 검색..."
            v-model="searchQuery"
            @input="filterFaqs"
          />
        </div>
        <div class="p-input-group">
          <i class="material-icons-round p-input-group__icon">category</i>
          <select class="p-input" v-model="selectedCategory" @change="filterFaqs">
            <option value="">전체 카테고리</option>
            <option v-for="category in categories" :key="category.value" :value="category.value">
              {{ $t(category.labelKey) }}
            </option>
          </select>
        </div>
        <div class="p-input-group">
          <i class="material-icons-round p-input-group__icon">visibility</i>
          <select class="p-input" v-model="statusFilter" @change="filterFaqs">
            <option value="">전체 상태</option>
            <option value="active">활성</option>
            <option value="inactive">비활성</option>
          </select>
        </div>
      </div>

      <div class="p-faqs__body">
        <div v-if="loading" class="p-faqs__loading">
          <div class="p-spinner"></div>
          <p>로딩 중...</p>
        </div>

        <AEmpty
          v-else-if="filteredFaqs.length === 0"
          title="검색 결과가 없습니다"
          desc="다른 검색어나 필터를 시도해보세요"
          mochi="search"
        />

        <div v-else class="p-faq-list">
          <div
            v-for="faq in paginatedFaqs"
            :key="faq.id"
            class="p-faq-item"
            :class="{ 'p-faq-item--open': expandedFaqId === faq.id, 'p-faq-item--inactive': !faq.is_active }"
          >
            <button class="p-faq-item__head" type="button" @click="toggleAccordion(faq.id)">
              <APill :kind="categoryPillKind(faq.category)" size="sm">{{ getCategoryLabel(faq.category) }}</APill>
              <span class="p-faq-item__question">{{ faq.question }}</span>
              <span class="p-faq-item__meta">
                <APill :kind="faq.is_active ? 'success' : 'muted'" size="sm">
                  {{ faq.is_active ? '활성' : '비활성' }}
                </APill>
                <span class="p-faq-item__views">
                  <i class="material-icons-round">visibility</i>
                  {{ faq.views || 0 }}
                </span>
                <i class="material-icons-round p-faq-item__chevron">expand_more</i>
              </span>
            </button>
            <div v-if="expandedFaqId === faq.id" class="p-faq-item__body">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div class="p-faq-item__answer" v-html="faq.answer" />
              <div class="p-faq-item__foot">
                <span>
                  <i class="material-icons-round">schedule</i>
                  최종 수정: {{ formatDate(faq.updated_at) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 페이징 -->
        <div v-if="filteredFaqs.length > itemsPerPage" class="p-faqs__pagination">
          <nav>
            <ul class="p-pagination">
              <li class="p-pagination__item" :class="{ 'p-pagination__item--disabled': currentPage === 1 }">
                <a class="p-pagination__link" @click="changePage(currentPage - 1)">
                  <i class="material-icons-round">chevron_left</i>
                </a>
              </li>
              <li
                v-for="page in totalPages"
                :key="page"
                class="p-pagination__item"
                :class="{ 'p-pagination__item--active': currentPage === page }"
              >
                <a class="p-pagination__link" @click="changePage(page)">{{ page }}</a>
              </li>
              <li class="p-pagination__item" :class="{ 'p-pagination__item--disabled': currentPage === totalPages }">
                <a class="p-pagination__link" @click="changePage(currentPage + 1)">
                  <i class="material-icons-round">chevron_right</i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </ACard>

  </div>
</template>

<script>
import Swal from 'sweetalert2';
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import AStat from '@/components/Pineapple/AStat.vue';
import APill from '@/components/Pineapple/APill.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';

export default {
  name: "HelpDeskFAQs",
  components: { APageHead, ACard, AStat, APill, AEmpty },
  data() {
    return {
      faqs: [],
      filteredFaqs: [],
      categories: [
        { value: 'general', labelKey: 'helpdesk_category.general' },
        { value: 'payment', labelKey: 'helpdesk_category.payment' },
        { value: 'technical', labelKey: 'helpdesk_category.technical' },
        { value: 'account', labelKey: 'helpdesk_category.account' },
        { value: 'course', labelKey: 'helpdesk_category.course' },
        { value: 'other', labelKey: 'helpdesk_category.other' }
      ],
      loading: false,
      searchQuery: '',
      selectedCategory: '',
      statusFilter: '',
      expandedFaqId: null,
      currentPage: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    activeFaqs() {
      return this.faqs.filter(faq => faq.is_active).length;
    },
    monthlyViews() {
      return this.faqs.reduce((sum, faq) => sum + (faq.views || 0), 0);
    },
    totalPages() {
      return Math.ceil(this.filteredFaqs.length / this.itemsPerPage);
    },
    paginatedFaqs() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredFaqs.slice(start, end);
    }
  },
  mounted() {
    this.loadFaqs();
  },
  methods: {
    async loadFaqs() {
      this.loading = true;
      try {
        const response = await this.$axios({
          method: 'get',
          url: '/helpdesk/faqs',
          loadingBar: true
        });

        this.faqs = response.data.faqs || [];
        this.filterFaqs();
      } catch (error) {
        console.error('FAQ 로딩 실패:', error);
        Swal.fire({
          icon: 'error',
          title: '로딩 실패',
          text: 'FAQ 목록을 불러오는데 실패했습니다.'
        });
      } finally {
        this.loading = false;
      }
    },
    filterFaqs() {
      let filtered = [...this.faqs];

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(faq =>
          faq.question.toLowerCase().includes(query) ||
          faq.answer.toLowerCase().includes(query)
        );
      }

      if (this.selectedCategory) {
        filtered = filtered.filter(faq => faq.category === this.selectedCategory);
      }

      if (this.statusFilter === 'active') {
        filtered = filtered.filter(faq => faq.is_active);
      } else if (this.statusFilter === 'inactive') {
        filtered = filtered.filter(faq => !faq.is_active);
      }

      filtered.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

      this.filteredFaqs = filtered;
      this.currentPage = 1;
    },
    toggleAccordion(faqId) {
      this.expandedFaqId = this.expandedFaqId === faqId ? null : faqId;
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    getCategoryLabel(categoryValue) {
      const category = this.categories.find(c => c.value === categoryValue);
      return category ? this.$t(category.labelKey) : categoryValue;
    },
    categoryPillKind(category) {
      const map = {
        general: 'info',
        payment: 'warning',
        technical: 'primary',
        account: 'success',
        course: 'accent',
        other: 'muted',
      };
      return map[category] || 'muted';
    },
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.p-faqs {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.p-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
}

.p-faqs__filters {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 12px;
  padding: 20px;
  border-bottom: 1px solid $p-line;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.p-faqs__body {
  padding: 20px;
}

.p-faqs__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
  color: $p-ink-soft;

  .p-spinner {
    width: 36px;
    height: 36px;
    border: 3px solid rgba($p-point, 0.2);
    border-top-color: $p-point;
    border-radius: 50%;
    animation: pineSpin 0.8s linear infinite;
  }
}

@keyframes pineSpin {
  to { transform: rotate(360deg); }
}

.p-faq-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.p-faq-item {
  background: #fff;
  border: 1px solid $p-line;
  border-radius: 14px;
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba($p-point, 0.5);
    box-shadow: 0 2px 8px rgba($p-ink, 0.04);
  }

  &--open {
    border-color: $p-point;
    box-shadow: 0 4px 14px rgba($p-point, 0.15);

    .p-faq-item__chevron {
      transform: rotate(180deg);
    }
  }

  &--inactive {
    opacity: 0.65;
  }
}

.p-faq-item__head {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 18px;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: $p-ink;
  font-weight: 600;

  &:hover {
    background: rgba($p-point, 0.04);
  }
}

.p-faq-item__question {
  flex: 1;
  line-height: 1.4;
}

.p-faq-item__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: $p-ink-soft;
  font-weight: 500;
  flex-shrink: 0;
}

.p-faq-item__views {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 14px;
  }
}

.p-faq-item__chevron {
  font-size: 20px;
  color: $p-ink-soft;
  transition: transform 0.2s ease;
}

.p-faq-item__body {
  padding: 0 18px 18px;
  border-top: 1px solid $p-line;
  padding-top: 16px;
}

.p-faq-item__answer {
  font-size: 14px;
  line-height: 1.65;
  color: $p-ink;
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: anywhere;

  :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
    margin: 12px 0 6px;
    font-weight: 700;
    color: $p-ink;
  }
  :deep(h1) { font-size: 1.25rem; }
  :deep(h2) { font-size: 1.125rem; }
  :deep(h3) { font-size: 1rem; }
  :deep(p) { margin: 6px 0; }
  :deep(ul), :deep(ol) {
    margin: 6px 0;
    padding-left: 1.25rem;
  }
  :deep(a) {
    color: $p-point-deep;
    text-decoration: underline;
  }
  :deep(code) {
    background: rgba($p-ink, 0.06);
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 0.85em;
  }
  :deep(pre) {
    background: $p-ink;
    color: #fff;
    border-radius: 8px;
    padding: 12px 14px;
    overflow: auto;
    font-size: 0.85em;
  }
  :deep(blockquote) {
    margin: 8px 0;
    padding: 8px 14px;
    background: rgba($p-point, 0.06);
    border-left: 3px solid $p-point;
    border-radius: 0 8px 8px 0;
  }
  :deep(img), :deep(video) {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 6px 0;
  }
  :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 6px 0;
  }
  :deep(th), :deep(td) {
    border: 1px solid $p-line;
    padding: 6px 10px;
  }
  :deep(th) {
    background: $p-bg;
    font-weight: 600;
  }
}

.p-faq-item__foot {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  font-size: 12px;
  color: $p-ink-soft;

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;

    i {
      font-size: 14px;
    }
  }
}

.p-faqs__pagination {
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid $p-line;
}

.p-pagination {
  display: flex;
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.p-pagination__item {
  &--disabled .p-pagination__link {
    opacity: 0.4;
    pointer-events: none;
  }
  &--active .p-pagination__link {
    background: $p-point;
    color: $p-ink;
    border-color: $p-point;
  }
}

.p-pagination__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid $p-line;
  border-radius: 8px;
  background: #fff;
  color: $p-ink;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: $p-point;
    background: rgba($p-point, 0.08);
  }

  i {
    font-size: 18px;
  }
}

</style>
